import {
  Button,
  ButtonLink,
  Card,
  CardContact,
  Content,
  FormMessage,
  Header,
  Icon,
  Modal,
  PhoneField,
  Spinner,
  Text,
  TextField,
  TipMessage,
} from '../common/components';
import { IconNames } from '../common/components/Icon/IconNames';
import routes from '../common/constants/routes';
import { useAppDispatch, useAppSelector, useDocumentTitle } from '../common/hooks';
import { t } from '../common/i18n';

import { IBuildingOverviewConstruction, IGetResBuildingOverview, TSFixMe } from '../frontend-libs/evlapp-types';

import { useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import {
  AdminInviteEmailStatus,
  checkAdminInviteEmail,
  getBuildingCommitte,
  getBuildingControlAuthority,
  getBuildingExternalContacts,
  getBuildingInfo,
  getBuildingOverview,
  inviteAdmin,
  removeAdminRole,
} from './services/BuildingService';
import { useModal } from '../common/components/Modal/useModal';
import { getProfileName } from '../common/utils/Profile/profile';
import { selectSecurity } from '../common/store/securitySlice';
import { setFlashMessage } from '../common/store/uiSlice';
import Yup, { backendViolations, YupEmail, YupPhone } from '../common/utils/FormValidation';
import { SubscriptionCard } from './components/SubscriptionCard';
import { isDemo } from '../common/constants/common';
import { formatDate } from '../common/utils/Date/Date';

const buildingAddresses = (data: TSFixMe[]) => {
  const addresses: string[] = [];

  data.forEach((building) => {
    if (building.name) addresses.push(building.name);
  });
  return addresses.join(', ');
};

const adminsList = (data: TSFixMe[]) => {
  const admins: string[] = [];

  data.forEach((admin) => {
    if (!admin.profile) return;

    const fullName = getProfileName({ ...admin.profile }) || admin.profile.email;
    return admins.push(fullName);
  });
  return admins.join(', ');
};

const contactList = ({ data, isLoading }: TSFixMe) => {
  if (data.length === 0 || isLoading) {
    return (
      <Card className="Card--content">
        <Text as="div" className="Flex" size="body-medium" color="Neutral600" weight="400">
          {!isLoading && t('Building.Detail.emptyContactList')}
          {isLoading && (
            <>
              <Spinner size="small" className="mr-1" /> {t('Building.Detail.isLoading')}
            </>
          )}
        </Text>
      </Card>
    );
  }

  return data.map((contact: TSFixMe, i: number) => {
    const isLast = i === data.length - 1;

    let position: null | string = null;
    if (contact.committeUserType) position = t(`Enums.ContactPositionsTypes.${contact.committeUserType}`);

    return <CardContact key={contact.id} {...contact} position={position} className={isLast && 'mb-0'} />;
  });
};

// RUIAN uses "not identified" / "undefined" codes for missing data - we hide those rows completely
const HIDDEN_ENUM_VALUES = ['not_identified', 'undefined'];

const yesNo = (value: string | null): string | null => {
  if (!value || HIDDEN_ENUM_VALUES.includes(value)) return null;
  if (value === 'with') return t('Building.Overview.yes');
  if (value === 'without') return t('Building.Overview.no');
  return null;
};

const enumValue = (value: string | null, enumName: string): string | null => {
  if (!value || HIDDEN_ENUM_VALUES.includes(value)) return null;
  return t(`Enums.${enumName}.${value}`);
};

const unitTypeIcon = (useType: string): IconNames => {
  switch (useType) {
    case 'flat':
      return 'home';
    case 'garage':
      return 'garage';
    case 'workshop':
      return 'settings';
    default:
      return 'building';
  }
};

const constructionRows = (construction: IBuildingOverviewConstruction | null) => {
  if (!construction) return [];

  const rows: { label: string; value: string }[] = [];
  const add = (labelKey: string, value: string | number | null) => {
    if (value === null || value === '') return;
    rows.push({ label: t(`Building.Overview.Fields.${labelKey}`), value: String(value) });
  };

  add('useOfConstruction', enumValue(construction.useOfConstruction, 'RuianUseOfConstruction'));
  add('countOfFloors', construction.countOfFloors);
  add('finishedAt', construction.finishedAt ? construction.finishedAt.substring(0, 4) : null);
  add('elevator', yesNo(construction.elevator));
  add('heating', enumValue(construction.heating, 'RuianHeating'));
  add('waterConnection', yesNo(construction.waterConnection));
  add('gasConnection', enumValue(construction.gasConnection, 'RuianGasConnection'));
  add('sewerConnection', enumValue(construction.sewerConnection, 'RuianSewerConnection'));
  add('floorArea', construction.floorArea ? `${construction.floorArea} m²` : null);
  add('buildUpArea', construction.buildUpArea ? `${construction.buildUpArea} m²` : null);
  add('enclosedArea', construction.enclosedArea ? `${construction.enclosedArea} m³` : null);

  return rows;
};

export const BuildingDetail: React.FC = () => {
  useDocumentTitle(t('Building.title'));

  const dispatch = useAppDispatch();
  const securityState = useAppSelector(selectSecurity);
  const security = securityState.ui;
  const isAdmin = security.isAdmin;
  const myProfileId = securityState.profile?.id ?? null;

  const [buildingInfo, setBuildingInfo] = useState<TSFixMe>({
    isLoading: true,
    data: { admins: [], addresses: [], units: [] },
  });
  const [overview, setOverview] = useState<{ isLoading: boolean; data: IGetResBuildingOverview | null }>({
    isLoading: true,
    data: null,
  });
  const [isTechOpen, setIsTechOpen] = useState<boolean>(false);
  const techRows = constructionRows(overview.data?.construction ?? null);
  const [committe, setCommitte] = useState<TSFixMe>({ isLoading: true, data: [] });
  const [controlAuthority, setControlAuthority] = useState<TSFixMe>({ isLoading: true, data: [] });
  const [externalContacts, setExternalContacts] = useState<TSFixMe>({ isLoading: true, data: [] });

  const [isShownInvite, toggleInvite] = useModal();
  const [isShownRemove, toggleRemove] = useModal();
  const [removeTarget, setRemoveTarget] = useState<TSFixMe>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Předběžná kontrola e-mailu — spouští se po dopsání (debounce) nebo při opuštění pole
  const [emailCheck, setEmailCheck] = useState<{ email: string; status: AdminInviteEmailStatus } | null>(null);
  const emailCheckTimer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (emailCheckTimer.current) window.clearTimeout(emailCheckTimer.current);
    },
    []
  );

  const runEmailCheck = (email: string) => {
    const value = email.trim();
    // API voláme až pro smysluplně vypadající e-mail
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setEmailCheck(null);
      return;
    }

    checkAdminInviteEmail(value)
      .then((res) => setEmailCheck({ email: value, status: res.status }))
      .catch(() => setEmailCheck(null));
  };

  const handleInviteEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    inviteFormik.handleChange(e);
    setEmailCheck(null);
    if (emailCheckTimer.current) window.clearTimeout(emailCheckTimer.current);
    const value = e.target.value;
    emailCheckTimer.current = window.setTimeout(() => runEmailCheck(value), 600);
  };

  const handleInviteEmailBlur = () => {
    if (emailCheckTimer.current) window.clearTimeout(emailCheckTimer.current);
    runEmailCheck(inviteFormik.values.email);
  };

  const refetchBuildingInfo = async () => {
    const data = await getBuildingInfo();
    setBuildingInfo({ isLoading: false, data });
  };

  const inviteFormik = useFormik<{ firstName: string; lastName: string; email: string; phone: string }>({
    initialValues: { firstName: '', lastName: '', email: '', phone: '' },
    validationSchema: Yup.object({
      firstName: Yup.string().required(t('Building.Admins.InviteModal.Fields.FirstName.required')),
      lastName: Yup.string().required(t('Building.Admins.InviteModal.Fields.LastName.required')),
      email: YupEmail,
      phone: YupPhone,
    }),
    onSubmit: (values) => {
      setInviteError(null);

      // Předběžná kontrola už problém našla a hláška je zobrazená — nemá smysl posílat submit do 422
      if (
        emailCheck &&
        emailCheck.email === values.email.trim() &&
        (emailCheck.status === 'owner' || emailCheck.status === 'already_admin')
      ) {
        inviteFormik.setSubmitting(false);
        return;
      }

      inviteAdmin(values)
        .then(async () => {
          await refetchBuildingInfo();
          inviteFormik.resetForm();
          toggleInvite();
          dispatch(setFlashMessage({ message: t('Building.Admins.InviteModal.Flash.success'), type: 'success' }));
        })
        .catch((error: TSFixMe) => {
          // BE vrací 422 s anglickou violation na poli email — mapujeme na české hlášky
          const emailViolation = error?.violations?.find((v: TSFixMe) => v.propertyPath === 'email');
          if (emailViolation && String(emailViolation.title).includes('owner account')) {
            inviteFormik.setFieldError('email', t('Building.Admins.InviteModal.errorOwnerEmail'));
          } else if (emailViolation && String(emailViolation.title).includes('already an admin')) {
            inviteFormik.setFieldError('email', t('Building.Admins.InviteModal.errorAlreadyAdmin'));
          } else if (backendViolations(inviteFormik, error) === 'valid') {
            setInviteError(t('Building.Admins.InviteModal.error'));
          }
        })
        .finally(() => inviteFormik.setSubmitting(false));
    },
  });

  const handleRemoveAdminRole = () => {
    if (!removeTarget) return;

    setIsRemoving(true);
    removeAdminRole(removeTarget.id)
      .then(async () => {
        await refetchBuildingInfo();
        toggleRemove();
        dispatch(setFlashMessage({ message: t('Building.Admins.RemoveModal.Flash.success'), type: 'success' }));
      })
      .catch(() => {
        dispatch(setFlashMessage({ message: t('Building.Admins.RemoveModal.Flash.error'), type: 'error' }));
      })
      .finally(() => setIsRemoving(false));
  };

  // TODO: check with Ondrej Bel. slow loading
  const [isLoading] = useState<boolean>(false);

  // Subscription state logic
  const hasSubscription = security.tokens?.filter((item) => item.type === 'building-subscription').length || 0;
  const hasPollVotes = security.tokens?.filter((item) => item.type === 'poll-publish').length || 0;

  // Get subscription tokens for dates
  const subscriptionTokens = security.tokens?.filter((item) => item.type === 'building-subscription') || [];
  const pollTokens = security.tokens?.filter((item) => item.type === 'poll-publish') || [];

  const renderSubscriptionCard = () => {
    if (buildingInfo.isLoading) {
      return (
        <Card className="Card--content">
          <Text as="div" className="Flex" size="body-medium" color="Neutral600" weight="400">
            <Spinner size="small" className="mr-1" /> {t('Building.BuildingInfo.isLoading')}
          </Text>
        </Card>
      );
    }

    // Máte k dispozici kompletní služby (bílá/šedá) - když má building-subscription
    if (hasSubscription) {
      // `tokens` nese jen aktuálně platné tokeny — navázané prodloužení (token s validFrom v budoucnu)
      // v nich není, konec licence včetně něj posílá BE v `licenseValidTo`.
      const tokenValidTo = subscriptionTokens[0]?.validTo
        ? typeof subscriptionTokens[0].validTo === 'string'
          ? subscriptionTokens[0].validTo
          : subscriptionTokens[0].validTo.toISOString().split('T')[0]
        : '1999-01-01';
      const expirationDate = security.licenseValidTo ?? tokenValidTo;
      return (
        <SubscriptionCard
          type="success"
          title="Máte k dispozici kompletní služby"
          expirationDate={expirationDate}
          openOrder={security.openOrder}
        />
      );
    }

    // Omezený režim aplikace (oranžová) - když nemá subscription ale má poll votes
    if (hasPollVotes) {
      const pollExpirationDate = pollTokens[0]?.validTo
        ? typeof pollTokens[0].validTo === 'string'
          ? pollTokens[0].validTo
          : pollTokens[0].validTo.toISOString().split('T')[0]
        : '1999-01-01';
      return (
        <SubscriptionCard
          type="warning"
          title="Aplikace je nyní v omezeném režimu"
          subtitle={`Dosud nevyužité jednorázové hlasování: ${pollTokens[0].remainingUses} (expirace ${formatDate(pollExpirationDate)})`}
          openOrder={security.openOrder}
        />
      );
    }

    // Neplacený režim aplikace (červená) - když nemá ani subscription ani poll votes
    return (
      <SubscriptionCard type="error" title="Aplikace je nyní v neplaceném režimu" openOrder={security.openOrder} />
    );
  };

  useEffect(() => {
    async function fetch() {
      const committe = await getBuildingCommitte();
      setCommitte({ isLoading: false, data: committe });
    }
    fetch();
  }, []);

  useEffect(() => {
    async function fetch() {
      const controlAuthority = await getBuildingControlAuthority();
      setControlAuthority({ isLoading: false, data: controlAuthority });
    }
    fetch();
  }, []);

  useEffect(() => {
    async function fetch() {
      const externalContacts = await getBuildingExternalContacts();
      setExternalContacts({ isLoading: false, data: externalContacts });
    }
    fetch();
  }, []);

  useEffect(() => {
    async function fetch() {
      const buildingInfo = await getBuildingInfo();
      setBuildingInfo({ isLoading: false, data: buildingInfo });
    }
    fetch();
  }, []);

  useEffect(() => {
    async function fetch() {
      const overview = await getBuildingOverview();
      setOverview({ isLoading: false, data: overview });
    }
    fetch();
  }, []);

  return (
    <>
      <Header title={t('Building.title')} customBacklink={routes.poll} />
      <Content isLoading={isLoading}>
        {/* demo ukazuje ukázkový dům — licence ani objednávka tam nedávají smysl */}
        {isAdmin && !isDemo && renderSubscriptionCard()}

        <Card className={`Card--content mb-1${isAdmin && !isDemo ? ' mt-3' : ''}`}>
          {buildingInfo.isLoading ? (
            <Text as="div" className="Flex" size="body-medium" color="Neutral600" weight="400">
              {buildingInfo.isLoading && (
                <>
                  <Spinner size="small" className="mr-1" /> {t('Building.BuildingInfo.isLoading')}
                </>
              )}
            </Text>
          ) : (
            <div className="BuildingHero">
              <Text size="h2" color="Neutral800">
                {buildingAddresses(buildingInfo.data.addresses)}
              </Text>
              <Text as="div" size="body-medium" color="Neutral600" weight="400" className="mt-1">
                {t('Building.demoCategory')} · {t('Building.BuildingInfo.admins')}:{' '}
                {adminsList(buildingInfo.data.admins)}
              </Text>

              {overview.data && (
                <>
                  <div className="Card__divider mt-2 mb-2" />
                  <div className="BuildingHero__stats">
                    {overview.data.unitCounts.map((unitCount) => (
                      <div key={unitCount.useType} className="BuildingHero__stat">
                        <Icon
                          name={unitTypeIcon(unitCount.useType)}
                          className="BuildingHero__statIcon"
                          color="Primary500"
                        />
                        <div>
                          <Text as="div" size="h2" color="Neutral800" className="BuildingHero__statValue">
                            {unitCount.count}
                          </Text>
                          <Text as="div" size="body-small" color="Neutral600" weight="500">
                            {t(`Building.Overview.UnitCounts.${unitCount.useType}`, undefined, {
                              count: unitCount.count,
                            })}
                          </Text>
                        </div>
                      </div>
                    ))}
                    <div className="BuildingHero__stat">
                      <Icon name="users" className="BuildingHero__statIcon" color="Primary500" />
                      <div>
                        <Text as="div" size="h2" color="Neutral800" className="BuildingHero__statValue">
                          {overview.data.ownersCount}
                        </Text>
                        <Text as="div" size="body-small" color="Neutral600" weight="500">
                          {t('Building.Overview.ownersLabel', undefined, { count: overview.data.ownersCount })}
                        </Text>
                      </div>
                    </div>
                    {isAdmin && (
                      <ButtonLink
                        title={t('Building.Overview.showUnits')}
                        to={routes.units}
                        variant="text"
                        iconAfter="arrowRightSmall"
                        className="BuildingHero__statsLink"
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {techRows.length > 0 && (
            <div className="BuildingTechnical">
              <div className="Card__divider mt-2 mb-2" />
              <div
                className="BuildingTechnical__toggle Flex FlexAlign--center FlexJustify--space-between"
                onClick={() => setIsTechOpen((prev) => !prev)}
              >
                <Text size="h3" color="Neutral750">
                  {t('Building.Overview.constructionTitle')}
                </Text>
                <Icon name={isTechOpen ? 'arrowUpSmall' : 'arrowDownSmall'} color="Neutral700" />
              </div>
              {isTechOpen && (
                <>
                  <div className="BuildingTechnical__grid mt-2">
                    {techRows.map((row) => (
                      <div key={row.label}>
                        <Text as="div" size="body-xs" color="Neutral600" weight="500">
                          {row.label}
                        </Text>
                        <Text as="div" size="body-medium" color="Neutral800" weight="600">
                          {row.value}
                        </Text>
                      </div>
                    ))}
                  </div>
                  <Text as="div" size="body-xs" color="Neutral600" weight="400" className="mt-2">
                    {t('Building.Overview.constructionSource')}
                  </Text>
                </>
              )}
            </div>
          )}
        </Card>

        {isAdmin && (
          <>
            <Text size="h2" color="Neutral800" className="Flex FlexAlign--center FlexJustify--space-between mt-3 mb-1">
              {t('Building.Admins.title')}
              <Button
                title={t('Building.Admins.invite')}
                variant="text"
                size="small"
                disabledDeps={['demo', 'expired']}
                onClick={() => {
                  setInviteError(null);
                  setEmailCheck(null);
                  inviteFormik.resetForm();
                  toggleInvite();
                }}
              />
            </Text>

            {buildingInfo.isLoading ? (
              <Card className="Card--content">
                <Text as="div" className="Flex" size="body-medium" color="Neutral600" weight="400">
                  <Spinner size="small" className="mr-1" /> {t('Building.Detail.isLoading')}
                </Text>
              </Card>
            ) : (
              buildingInfo.data.admins.map((admin: TSFixMe, i: number) => {
                const profile = admin.profile;
                if (!profile) return null;

                const isMe = profile.id === myProfileId;
                const isLast = i === buildingInfo.data.admins.length - 1;

                return (
                  <CardContact
                    key={profile.id}
                    firstName={profile.firstName}
                    lastName={profile.lastName}
                    email={profile.email}
                    position={
                      isMe
                        ? t('Building.Admins.you')
                        : profile.isUser
                          ? t('Building.Admins.registered')
                          : t('Building.Admins.notRegistered')
                    }
                    className={isLast ? 'mb-0' : undefined}
                    action={
                      !isMe ? (
                        <Button
                          title={t('Building.Admins.remove')}
                          variant="text"
                          size="small"
                          disabledDeps={['demo', 'expired']}
                          onClick={() => {
                            setRemoveTarget(profile);
                            toggleRemove();
                          }}
                        />
                      ) : undefined
                    }
                  />
                );
              })
            )}
          </>
        )}

        <Text size="h2" color="Neutral800" className="Flex FlexAlign--center FlexJustify--space-between mt-3 mb-1">
          {t('Building.Detail.committeMembers')}
          {isAdmin && (
            <ButtonLink
              title={t('Building.Detail.updateContacts')}
              to="contacts/committe"
              variant="text"
              iconAfter="arrowRightSmall"
            />
          )}
        </Text>

        {contactList(committe)}

        <Text size="h2" color="Neutral800" className="Flex FlexAlign--center FlexJustify--space-between mt-3 mb-1">
          {t('Building.Detail.controlAuthority')}

          {isAdmin && (
            <ButtonLink
              title={t('Building.Detail.updateContacts')}
              to="contacts/control-authority"
              variant="text"
              iconAfter="arrowRightSmall"
            />
          )}
        </Text>

        {contactList(controlAuthority)}

        <Text size="h2" color="Neutral800" className="Flex FlexAlign--center FlexJustify--space-between mt-3 mb-1">
          {t('Building.Detail.externalContacts')}

          {isAdmin && (
            <ButtonLink
              title={t('Building.Detail.updateContacts')}
              to="contacts/external-contact"
              variant="text"
              iconAfter="arrowRightSmall"
            />
          )}
        </Text>
        {contactList(externalContacts)}

        <Modal isShown={isShownInvite} onHide={toggleInvite}>
          <Text size="h2" align="center" className="mb-2">
            {t('Building.Admins.InviteModal.title')}
          </Text>
          <Text size="body-medium" color="Neutral700" align="center" className="mb-3">
            {t('Building.Admins.InviteModal.subtitle')}
          </Text>
          <form onSubmit={inviteFormik.handleSubmit}>
            <TextField
              name="firstName"
              label={t('Building.Admins.InviteModal.Fields.FirstName.label')}
              value={inviteFormik.values.firstName}
              onChange={inviteFormik.handleChange}
              error={{ touched: inviteFormik.touched.firstName, message: inviteFormik.errors.firstName }}
              className="mb-2"
            />
            <TextField
              name="lastName"
              label={t('Building.Admins.InviteModal.Fields.LastName.label')}
              value={inviteFormik.values.lastName}
              onChange={inviteFormik.handleChange}
              error={{ touched: inviteFormik.touched.lastName, message: inviteFormik.errors.lastName }}
              className="mb-2"
            />
            <TextField
              name="email"
              type="email"
              label={t('Building.Admins.InviteModal.Fields.Email.label')}
              value={inviteFormik.values.email}
              onChange={handleInviteEmailChange}
              onBlur={handleInviteEmailBlur}
              error={{ touched: inviteFormik.touched.email, message: inviteFormik.errors.email }}
              className="mb-2"
            />
            {emailCheck && emailCheck.email === inviteFormik.values.email.trim() && emailCheck.status === 'owner' && (
              <FormMessage message={t('Building.Admins.InviteModal.errorOwnerEmail')} variant="dark" className="mb-2" />
            )}
            {emailCheck &&
              emailCheck.email === inviteFormik.values.email.trim() &&
              emailCheck.status === 'already_admin' && (
                <FormMessage
                  message={t('Building.Admins.InviteModal.errorAlreadyAdmin')}
                  variant="dark"
                  className="mb-2"
                />
              )}
            {emailCheck &&
              emailCheck.email === inviteFormik.values.email.trim() &&
              emailCheck.status === 'existing_admin' && (
                <TipMessage className="mb-2">{t('Building.Admins.InviteModal.infoExistingAdmin')}</TipMessage>
              )}
            <PhoneField
              name="phone"
              label={t('Building.Admins.InviteModal.Fields.Phone.label')}
              value={inviteFormik.values.phone}
              // PhoneField volá onChange se STRINGEM (E164) nebo undefined při smazání — viz past v CLAUDE.md
              onChange={(phone) => inviteFormik.setFieldValue('phone', phone ?? '')}
              error={{ touched: inviteFormik.touched.phone, message: inviteFormik.errors.phone }}
              className="mb-2"
            />
            {inviteError && <FormMessage message={inviteError} variant="dark" />}
            <div className="Flex FlexColumn FlexAlign--center mt-2">
              <Button
                title={t('Building.Admins.InviteModal.submit')}
                wide={true}
                onClick={inviteFormik.handleSubmit}
                loading={inviteFormik.isSubmitting}
                disabledDeps={['demo', 'expired']}
              />
            </div>
          </form>
        </Modal>

        <Modal isShown={isShownRemove} onHide={toggleRemove}>
          <Text size="h2" align="center" className="mb-2">
            {t('Building.Admins.RemoveModal.title')}
          </Text>
          <Text size="body-medium" color="Neutral700" align="center" className="mb-3">
            {t('Building.Admins.RemoveModal.subtitle', undefined, {
              name: removeTarget ? getProfileName({ ...removeTarget }) || removeTarget.email : '',
            })}
          </Text>
          <div className="Flex FlexColumn FlexAlign--center">
            <Button
              title={t('Building.Admins.RemoveModal.confirm')}
              wide={true}
              onClick={handleRemoveAdminRole}
              loading={isRemoving}
              disabledDeps={['demo', 'expired']}
            />
          </div>
        </Modal>
      </Content>
    </>
  );
};
